import { z } from 'zod';

// Variable sources used by the 'builder' approval resolver (docs/maker-checker-design.md
// "Template builder resolver"). Kind-specific requirements (e.g. task_attr needs `field`)
// are enforced in the superRefine below rather than here, since they depend on `kind`.
const approvalVariableSourceSchema = z.object({
  kind: z.enum([
    'static', 'maker_name', 'task_name', 'current_value', 'task_value', 'task_attr',
    'image_link', 'current_upload', 'generated_image', 'approval_decision',
  ]),
  static: z.string().optional(),
  taskRef: z.string().optional(),
  field: z.string().optional(),
  decision: z.enum(['approved', 'rejected']).optional(),
});

const approvalTemplateButtonSchema = z.object({
  subType: z.enum(['quick_reply']),
  // Ordered, concatenated with no separator — lets a payload mix literal text with a
  // resolved value, e.g. a static "/start CODE " piece followed by a task_value piece.
  payload: z.array(approvalVariableSourceSchema),
  uniqueAction: z.boolean().optional(),
});

const approvalTemplateBuilderSchema = z.object({
  header: z.object({
    type: z.enum(['image', 'document', 'video', 'text']),
    source: approvalVariableSourceSchema,
    valueSource: approvalVariableSourceSchema.optional(),
  }).optional(),
  body: z.array(approvalVariableSourceSchema),
  buttons: z.array(approvalTemplateButtonSchema).optional(),
});

type ApprovalVariableSourceInput = z.infer<typeof approvalVariableSourceSchema>;

// Validates one ApprovalVariableSource's kind-specific fields. `textOnly` restricts to the
// kinds valid for a body variable or a header's valueSource (the media kinds — image_link,
// current_upload, generated_image — are only valid as a header's own source).
// `allowDecision` additionally permits approval_decision — valid only in a button's payload
// (on a maker-checker level), never in a body or header.
function validateApprovalVariableSource(
  ctx: z.RefinementCtx,
  variable: ApprovalVariableSourceInput,
  path: (string | number)[],
  textOnly: boolean,
  allowDecision = false,
) {
  const textKinds = ['static', 'maker_name', 'task_name', 'current_value', 'task_value', 'task_attr'];
  const allowedKinds = allowDecision ? [...textKinds, 'approval_decision'] : textKinds;
  if (textOnly && !allowedKinds.includes(variable.kind)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${variable.kind} can only be used as a header source, not here`,
      path: [...path, 'kind'],
    });
    return;
  }
  if (variable.kind === 'static' && !variable.static?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Static text is required',
      path: [...path, 'static'],
    });
  }
  if (variable.kind === 'task_attr' && !variable.field?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A field name is required to read an attribute from a task',
      path: [...path, 'field'],
    });
  }
  if (variable.kind === 'approval_decision' && !variable.decision) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A decision (approved or rejected) is required',
      path: [...path, 'decision'],
    });
  }
}

type ApprovalTemplateBuilderInput = z.infer<typeof approvalTemplateBuilderSchema>;

// Validates a templateBuilder wherever it's read from — an ApprovalLevel (the maker-checker
// chain) or approvalConfig's own flat field (the one-way TEAM_ALERT/NOTIFY send, which has no
// levels). `path` is the RHF field path up to and including `templateBuilder`.
function validateApprovalTemplateBuilder(
  ctx: z.RefinementCtx,
  builder: ApprovalTemplateBuilderInput | undefined,
  path: (string | number)[],
) {
  if (!builder || builder.body.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'At least one body variable is required for the template builder',
      path: [...path, 'body'],
    });
  } else {
    builder.body.forEach((variable, varIndex) => {
      validateApprovalVariableSource(ctx, variable, [...path, 'body', varIndex], true);
    });
  }
  if (builder?.header) {
    const headerPath = [...path, 'header'];
    if (builder.header.type === 'text') {
      // A text header reads its source the same way a body variable does — no media kinds,
      // no value_source (that's only for rendering a value into a generated image).
      validateApprovalVariableSource(ctx, builder.header.source, [...headerPath, 'source'], true);
    } else if (!['image_link', 'current_upload', 'generated_image'].includes(builder.header.source.kind)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Header source must be image_link, current_upload, or generated_image',
        path: [...headerPath, 'source', 'kind'],
      });
    } else if (builder.header.source.kind === 'generated_image') {
      if (!builder.header.valueSource) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'A value is required to render as the generated image',
          path: [...headerPath, 'valueSource'],
        });
      } else {
        validateApprovalVariableSource(ctx, builder.header.valueSource, [...headerPath, 'valueSource'], true);
      }
    }
  }
  builder?.buttons?.forEach((button, btnIndex) => {
    const buttonPath = [...path, 'buttons', btnIndex];
    if (button.payload.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one payload variable is required for a button',
        path: [...buttonPath, 'payload'],
      });
    } else {
      button.payload.forEach((variable, varIndex) => {
        validateApprovalVariableSource(ctx, variable, [...buttonPath, 'payload', varIndex], true, true);
      });
    }
  });
}

// Shared task-form validation schema consumed by both workflow-builder
// generations: the V3 TaskModal (`taskSchema`) and the V4 TaskConfigPanel
// (`panelSchema`). Keeping a single source of truth prevents the two forms
// from drifting so that one accepts what the other rejects (e.g. a blank task
// name or an auto-skip step with no condition task).
export const documentLinkUrlSchema = z.string().url('Must be a valid URL');

export const taskFormSchema = z.object({
  name: z.string()
    .min(1, 'Task name is required')
    // Zod 4's .max() counts code points; the task grid and this limit use .length.
    .refine((name) => name.length <= 24, 'Task name must be 24 characters or less'),
  description: z.string()
    .max(480, 'Description must be 480 characters or less')
    .optional(),
  type: z.string().min(1, 'Task type is required'),
  data: z.string().optional(),
  actions: z.array(z.string()).default([]),
  approvalConfig: z.object({
    // Capped at 5 (docs/maker-checker-design.md Q2's default-if-unanswered) — no confirmed
    // customer need for more, and the payload's level segment is [0-9]{1,2} regardless.
    levels: z.array(z.object({
      level: z.number(),
      name: z.string().optional(),
      contactType: z.enum(['list', 'manual']).optional(),
      approvalContacts: z.array(z.object({
        name: z.string(),
        phone: z.string(),
      })).optional(),
      approvalTemplate: z.string().optional(),
      rejectTemplate: z.string().optional(),
      approvalResolver: z.string().optional(),
      templateBuilder: approvalTemplateBuilderSchema.optional(),
      quorum: z.enum(['any', 'all', 'm_of_n']).optional(),
      quorumCount: z.number().int().positive().optional(),
      timeoutHours: z.number().optional(),
      onTimeout: z.string().optional(),
      escalateToLevel: z.number().optional(),
      onReject: z.string().optional(),
    })).max(5, 'Up to 5 approval levels are supported').optional(),
    completionPolicy: z.enum(['on_submit', 'on_final_approval']).optional(),
    maxBounces: z.number().optional(),
    contactType: z.enum(['list', 'manual']).optional(),
    approvalContacts: z.array(z.object({
      name: z.string(),
      phone: z.string(),
    })).optional(),
    approvalTemplate: z.string().optional(),
    approvalResolver: z.string().optional(),
    // Read by the one-way TEAM_ALERT/NOTIFY send (team_alert.go), which has no levels —
    // levels[].templateBuilder above is for the maker-checker chain instead.
    templateBuilder: approvalTemplateBuilderSchema.optional(),
    parserEnabled: z.boolean().optional(),
    parserType: z.string().optional(),
    valueSourceTaskId: z.string().optional(),
    makerPendingText: z.string().optional(),
    makerApprovedText: z.string().optional(),
    makerRejectedText: z.string().optional(),
    approverApprovedText: z.string().optional(),
    approverRejectedText: z.string().optional(),
  }).optional(),
  validationConfig: z.object({
    type: z.enum(['less_than', 'greater_than', 'greater_than_or_equal', 'less_than_or_equal', 'between']).optional(),
    threshold: z.number().optional(),
    // Only used when type is 'between' — the inclusive upper bound, threshold being the lower.
    max_threshold: z.number().optional(),
    reference_task: z.number().optional(),
    error_message: z.string().optional(),
  }).optional(),
  extractionConfig: z.object({
    enabled: z.boolean().default(false),
    parser_type: z.enum(['gov_id', 'document', 'pdf_extract', 'face_present']).optional(),
    // Only meaningful when parser_type is gov_id — which specific ID to check the
    // extracted number's length/format against.
    gov_id_type: z.enum(['aadhaar', 'pan', 'passport', 'driving_licence', 'voter_id']).optional(),
    gate: z.boolean().optional(),
    required_fields: z.array(z.string()).optional(),
    // Extracted when present but never checked for absence — see required_fields for
    // the subset that actually blocks the gate.
    optional_fields: z.array(z.string()).optional(),
    // Off by default and not the primary gate: self-reported model confidence is
    // weakly calibrated, so required_fields is the signal that actually discriminates.
    min_confidence: z.number().min(0).max(1).optional(),
    store_result: z.boolean().optional(),
    failure_message: z.string().max(1024).optional(),
    // Failed extractions to allow before the upload is accepted anyway rather than
    // rejected forever. Unset means unlimited (falls back to the org default server-side).
    max_attempts: z.number().int().min(1).optional(),
  }).optional(),
  dataSourceConfig: z.object({
    source: z.string().optional(),
    locationTask: z.number().optional(),
    regionTask: z.number().optional(),
    radiusKm: z.number().optional(),
    // Static upper bound covers both ceilings (native channel: 50, paginated across
    // WhatsApp list messages in pages of 10; WhatsApp Flow's Dropdown component: 200
    // without images — see platform/datasource.go's nativeListMaxLimit/flowMaxLimit);
    // the tighter native cap is enforced contextually by the UI
    // (DataSourceConfigSection), not here, since this schema doesn't know whether
    // Flows are enabled.
    limit: z.number().max(200).optional(),
    emptyMessage: z.string().optional(),
    dataset: z.string().optional(),
    filterTask: z.number().optional(),
    lookupField: z.string().optional(),
    lookupFields: z.array(z.string()).optional(),
    matchMode: z.enum(['exact', 'search']).optional(),
    titleField: z.string().optional(),
    descriptionField: z.string().optional(),
    mode: z.string().optional(),
    answerField: z.string().optional(),
    fieldFilters: z.array(z.object({ field: z.string(), value: z.string() })).optional(),
  }).optional(),
  startnewConfig: z.object({
    targetChatbot: z.string().optional(),
    targetMappingSource: z.string().optional(),
    targetTask: z.number().optional(),
    defaultTargetKey: z.string().optional(),
    attributeMapping: z.record(z.string(), z.string()).optional(),
  }).optional(),
  autoFillConfig: z.record(z.string(), z.string()).optional(),
  skipConfig: z.object({
    conditions: z.array(z.object({
      conditionTask: z.number().optional(),
      conditionOperator: z.enum(['equals', 'not_equals', 'contains', 'not_contains', 'empty', 'not_empty', 'less_than', 'less_than_or_equal', 'greater_than', 'greater_than_or_equal']).optional(),
      conditionValue: z.string().optional(),
      conditionValueTask: z.number().optional(),
    })).optional().default([]),
    conditionLogic: z.enum(['and', 'or']).optional(),
    skipMode: z.enum(['auto', 'manual']).optional(),
  }).optional().refine(
    (data) => !(data?.skipMode === 'auto' && !data?.conditions?.some((condition) => condition.conditionTask !== undefined)),
    { message: 'At least one condition is required for auto-skip mode', path: ['conditions'] }
  ),
  counterConfig: z.object({
    identityTaskId: z.string().optional(),
    countField: z.string().optional(),
    codeTaskId: z.string().optional(),
    // Comma-separated in the form; TaskConfigPanel splits it into CounterConfigUI.sharedIdentities.
    sharedIdentities: z.string().optional(),
  }).optional(),
  writebackConfigs: z.array(z.object({
    dataset: z.string().optional().default(''),
    keyRef: z.string().optional().default(''),
    matchField: z.string().optional(),
    mode: z.enum(['update', 'upsert', 'create']).optional(),
    labelRef: z.string().optional(),
    parentKeyRef: z.string().optional(),
    fields: z.record(z.string(), z.string()).optional().default({}),
  })).optional().default([]),
  mutable: z.boolean().default(false),
  maskOutput: z.boolean().default(false),
  requirePrefill: z.boolean().default(false),
  subtasks: z.array(z.any()).optional(),
  softTtlValue: z.number().int().positive().optional(),
  softTtlUnit: z.enum(['hours', 'days']).optional(),
  documentLinks: z.array(z.object({ url: documentLinkUrlSchema })).optional().default([]),
}).superRefine((data, ctx) => {
  // TEAM_ALERT (mapped runtime action 'NOTIFY') reuses approvalConfig purely as a
  // contact list + template. When enabled, both must be provided — there is no
  // silent fallback to an approval template.
  if ((data.actions ?? []).includes('NOTIFY')) {
    const contacts = data.approvalConfig?.approvalContacts ?? [];
    if (contacts.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one contact is required for a team alert',
        path: ['approvalConfig', 'approvalContacts'],
      });
    }
    const template = data.approvalConfig?.approvalTemplate ?? '';
    if (template.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A template name is required for a team alert',
        path: ['approvalConfig', 'approvalTemplate'],
      });
    }

    // Team alerts have no levels, so a 'builder' resolver reads approvalConfig's own flat
    // templateBuilder rather than levels[].templateBuilder — validated the same way.
    if (data.approvalConfig?.approvalResolver === 'builder') {
      validateApprovalTemplateBuilder(ctx, data.approvalConfig?.templateBuilder, ['approvalConfig', 'templateBuilder']);
    }
  }

  // WRITEBACK needs at least one policy, and each policy needs a target dataset, a
  // key reference to resolve which row to patch, and at least one field to write —
  // an empty policy would be a silent no-op. A policy that adds rows needs a label
  // for them instead of fields; create mode may leave the key blank to generate one.
  if ((data.actions ?? []).includes('WRITEBACK')) {
    const policies = data.writebackConfigs ?? [];
    if (policies.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one writeback policy is required',
        path: ['writebackConfigs'],
      });
    }
    policies.forEach((policy, index) => {
      if (!policy.dataset?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'A target dataset is required for master-data writeback',
          path: ['writebackConfigs', index, 'dataset'],
        });
      }
      const addsRows = policy.mode === 'upsert' || policy.mode === 'create';
      if (!policy.keyRef?.trim() && policy.mode !== 'create') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'A key reference is required for master-data writeback',
          path: ['writebackConfigs', index, 'keyRef'],
        });
      }
      if (addsRows && !(policy.mode === 'upsert' && policy.matchField === 'label') && !policy.labelRef?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'A label reference is required to add rows',
          path: ['writebackConfigs', index, 'labelRef'],
        });
      }
      if (!addsRows && !Object.keys(policy.fields ?? {}).length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one field is required for master-data writeback',
          path: ['writebackConfigs', index, 'fields'],
        });
      }
    });
  }

  // Each level needs at least one approver, and m_of_n's count must be reachable — otherwise
  // the backend silently clamps it (NormalizeLevels), which is safe but surprising to an
  // author who typed a number that quietly became a different one.
  (data.approvalConfig?.levels ?? []).forEach((level, index) => {
    const contactCount = level.approvalContacts?.length ?? 0;
    if (contactCount === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one approver is required for this level',
        path: ['approvalConfig', 'levels', index, 'approvalContacts'],
      });
    }
    if (level.quorum === 'm_of_n') {
      const count = level.quorumCount ?? 0;
      if (count < 1 || (contactCount > 0 && count > contactCount)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Must be between 1 and ${contactCount || 1}, the number of approvers at this level`,
          path: ['approvalConfig', 'levels', index, 'quorumCount'],
        });
      }
    }

    // A level using the 'builder' resolver needs a well-formed templateBuilder: at least one
    // body variable, and every variable's kind-specific fields present (mirrors the Go
    // resolveTextVariable/buildHeader validation in internal/workflow/messages/builder_resolver.go
    // so a misconfigured level is caught here rather than failing silently at send time).
    if (level.approvalResolver === 'builder') {
      validateApprovalTemplateBuilder(ctx, level.templateBuilder, ['approvalConfig', 'levels', index, 'templateBuilder']);
    }
  });

  // WIP and maker-checker both hold a task open using notes_enabled_mode, and the WIP
  // two-tap confirm completes the rep without consulting the approval config — so a task
  // waiting on an approver would let the submitter tap "done" and complete their own
  // request. The backend rejects the pair as well; this is the copy the author actually
  // reads. Sequencing the two deliberately is future work.
  const actions = data.actions ?? [];
  if (actions.includes('WIP') && actions.includes('MAKERCHECKER')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'WIP and maker-checker cannot be combined: both hold the task open, and together they let the submitter complete their own request without an approver seeing it',
      path: ['actions'],
    });
  }
});
